import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import api, { formatApiError } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/constants";

const empty = {
  title: "", category: "cursos", description: "", short_description: "",
  price_usd: 0, duration: "", level: "", images: "", instructor: "", featured: false,
};

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited or 'new'
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(empty);
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({ ...p, images: (p.images || []).join(", ") });
    setEditing(p.id);
  };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price_usd: parseFloat(form.price_usd) || 0,
      images: form.images.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        await api.post("/products", payload);
        toast.success("Programa creado");
      } else {
        await api.put(`/products/${editing}`, payload);
        toast.success("Programa actualizado");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este programa?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Eliminado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <AppHeader title="Admin · Programas" showBack showLogo={false} />
      <main className="page-scroll px-4 pt-3" data-testid="admin-page">
        <button onClick={openNew} data-testid="admin-new-btn" className="gold-btn w-full py-3 flex items-center justify-center gap-2">
          <Plus size={16} /> Nuevo programa
        </button>

        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="text-slate-500 text-sm text-center py-8">Cargando...</div>
          ) : (
            products.map((p) => (
              <div key={p.id} className="card-clean p-3 flex items-center gap-3" data-testid={`admin-product-${p.id}`}>
                <img src={p.images?.[0]} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm text-[#0A2A66] truncate">{p.title}</div>
                  <div className="text-xs text-slate-500">{CATEGORY_LABELS[p.category]} · ${p.price_usd}</div>
                </div>
                <button onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="w-9 h-9 rounded-full bg-[#EEF3FF] text-[#0A2A66] flex items-center justify-center">
                  <Pencil size={14} />
                </button>
                <button onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={close}>
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-modal">
            <div className="sticky top-0 bg-white px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-heading font-bold text-[#0A2A66]">{editing === "new" ? "Nuevo programa" : "Editar programa"}</h3>
              <button onClick={close} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={save} className="p-5 space-y-3">
              <Field label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="form-title" required />
              <div>
                <label className="text-xs font-semibold text-[#0A2A66] uppercase tracking-wider">Categoría</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="form-category"
                  className="input-clean mt-1"
                >
                  {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                    <option key={slug} value={slug}>{label}</option>
                  ))}
                </select>
              </div>
              <Field label="Precio (USD)" type="number" step="0.01" value={form.price_usd} onChange={(v) => setForm({ ...form, price_usd: v })} testid="form-price" required />
              <Field label="Descripción corta" value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} testid="form-short" />
              <div>
                <label className="text-xs font-semibold text-[#0A2A66] uppercase tracking-wider">Descripción larga</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="form-description"
                  className="input-clean mt-1 min-h-[100px]"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Duración" value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} testid="form-duration" />
                <Field label="Nivel" value={form.level} onChange={(v) => setForm({ ...form, level: v })} testid="form-level" />
              </div>
              <Field label="Instructor" value={form.instructor} onChange={(v) => setForm({ ...form, instructor: v })} testid="form-instructor" />
              <Field label="URLs de imágenes (separadas por coma)" value={form.images} onChange={(v) => setForm({ ...form, images: v })} testid="form-images" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
                Marcar como destacado
              </label>
              <div className="flex gap-2 pt-2">
                <button type="button" onClick={close} className="flex-1 py-3 rounded-full border border-slate-200 font-semibold">Cancelar</button>
                <button type="submit" data-testid="form-save" className="gold-btn flex-1 py-3">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value, onChange, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold text-[#0A2A66] uppercase tracking-wider">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="input-clean mt-1" {...rest} />
    </div>
  );
}
